import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const OPT_OUT_KEY = 'aquavai-shipping-protection-user-opt-out-v2';
const PROTECTION_HANDLE = 'aquavai-shipping-protection';
const UPDATE_SOURCE = 'aquavai-shipping-protection';

// Shared across mounted cards and section morphs: preflight and mutations must
// finish before another protection operation reads the cart or adds a variant.
let protectionQueue = Promise.resolve();

function protectionCards() {
  return Array.from(document.querySelectorAll('aquavai-shipping-protection'));
}

function setProtectionBusy(busy) {
  for (const card of protectionCards()) card.setBusy(busy);
}

function syncProtectionCards(cart) {
  const line = cart.items.find((item) => item.handle === PROTECTION_HANDLE);
  const eligible = cart.items.some((item) => item.handle !== PROTECTION_HANDLE);
  for (const card of protectionCards()) card.syncCart(line, eligible);
}

function sectionIds() {
  return [...new Set(Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId).filter(Boolean))];
}

async function fetchProtectionCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('Unable to refresh Shipping Protection cart state');
  return response.json();
}

async function mutateProtection(action, payload, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines,
    promise: deferred.promise,
  }));

  try {
    const response = await fetch(action === 'add' ? Theme.routes.cart_add_url : Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ ...payload, sections: sectionIds().join(','), sections_url: window.location.pathname }),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || result.errors || 'Unable to update Shipping Protection');
    }

    const cart = action === 'add' ? await fetchProtectionCart() : result;
    syncProtectionCards(cart);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: result.sections,
        source: UPDATE_SOURCE,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    // A failed/ambiguous request is not proof of an ON or OFF state. Recover
    // from Shopify when possible, including a lost response after a real add.
    try {
      const cart = await fetchProtectionCart();
      syncProtectionCards(cart);
      // Refetch sections so totals and rows also recover after an ambiguous POST.
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', sectionIds().join(','));
      const sectionsResponse = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
      if (!sectionsResponse.ok) throw new Error('Unable to refresh cart sections');
      const sections = await sectionsResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, sections, source: UPDATE_SOURCE, didError: true },
      });
    } catch {
      deferred.reject(error);
    }
    throw error;
  }
}

async function reconcileProtection() {
  const cart = await fetchProtectionCart();
  syncProtectionCards(cart);
  const protection = cart.items.filter((item) => item.handle === PROTECTION_HANDLE);
  const eligible = cart.items.some((item) => item.handle !== PROTECTION_HANDLE);
  // Protection requires an explicit ON choice; an unset preference defaults OFF.
  const optedOut = sessionStorage.getItem(OPT_OUT_KEY) !== 'false';

  if (!eligible || optedOut) {
    // Remove all protection-only lines, including any pre-existing separate keys.
    // Keep explicit opt-out for the shopping session, even if the cart empties.
    for (const line of protection) {
      await mutateProtection('remove', { id: line.key, quantity: 0 }, [{ id: line.key, quantity: 0 }]);
    }
    return;
  }
  if (protection.length) return;

  const variantId = protectionCards().find((card) => card.dataset.variantId)?.dataset.variantId;
  // Empty-cart markup deliberately has no card. A newly rendered card retries
  // on connection once Shopify has supplied the real protection variant ID.
  if (!variantId || sessionStorage.getItem(OPT_OUT_KEY) !== 'false') return;
  await mutateProtection('add', { items: [{ id: Number(variantId), quantity: 1 }] },
    [{ merchandiseId: variantId, quantity: 1 }]);
}

function queueProtectionUpdate() {
  const task = protectionQueue.then(async () => {
    setProtectionBusy(true);
    try {
      await reconcileProtection();
    } finally {
      setProtectionBusy(false);
    }
  });
  protectionQueue = task.catch((error) => {
    document.dispatchEvent(new CartErrorEvent({
      error: error?.message || 'Unable to update Shipping Protection',
      code: 'SERVICE_UNAVAILABLE',
    }));
    console.error('[Aquavai Shipping Protection]', error);
  });
  return protectionQueue;
}

class AquavaiShippingProtection extends HTMLElement {
  #toggle;
  #dialog;
  #learnMore;
  #closeButton;

  connectedCallback() {
    this.#toggle = this.querySelector('[data-aq-protection-toggle]');
    this.#dialog = this.querySelector('[data-aq-protection-dialog]');
    this.#learnMore = this.querySelector('[data-aq-protection-learn-more]');
    this.#closeButton = this.querySelector('[data-aq-protection-close]');
    this.#toggle?.addEventListener('click', this.#onToggle);
    this.#learnMore?.addEventListener('click', this.#openDialog);
    this.#closeButton?.addEventListener('click', this.#closeDialog);
    this.#dialog?.addEventListener('click', this.#onDialogClick);
    this.#setToggle(this.dataset.inCart === 'true');
    queueProtectionUpdate();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
    this.#learnMore?.removeEventListener('click', this.#openDialog);
    this.#closeButton?.removeEventListener('click', this.#closeDialog);
    this.#dialog?.removeEventListener('click', this.#onDialogClick);
  }

  syncCart(line, eligible) {
    this.dataset.inCart = line ? 'true' : 'false';
    this.dataset.lineKey = line?.key || '';
    this.dataset.hasProtectableItems = eligible ? 'true' : 'false';
    this.hidden = !eligible;
    this.#setToggle(Boolean(line));
  }

  #setToggle(checked) {
    this.#toggle?.setAttribute('aria-checked', checked ? 'true' : 'false');
    this.#toggle?.classList.toggle('is-on', checked);
  }

  setBusy(busy) {
    this.classList.toggle('is-loading', busy);
    if (this.#toggle) {
      this.#toggle.disabled = busy;
      this.#toggle.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
  }

  #onToggle = () => {
    if (this.#toggle?.disabled) return;
    if (this.dataset.inCart === 'true') sessionStorage.setItem(OPT_OUT_KEY, 'true');
    else sessionStorage.setItem(OPT_OUT_KEY, 'false');
    // Preserve confirmed appearance while the request is pending.
    this.setBusy(true);
    queueProtectionUpdate();
  };

  #openDialog = () => {
    if (this.#dialog instanceof HTMLDialogElement && !this.#dialog.open) this.#dialog.showModal();
  };
  #closeDialog = () => {
    if (this.#dialog instanceof HTMLDialogElement) this.#dialog.close();
  };
  #onDialogClick = (event) => {
    if (event.target === this.#dialog) this.#dialog.close();
  };
}

if (!customElements.get('aquavai-shipping-protection')) {
  customElements.define('aquavai-shipping-protection', AquavaiShippingProtection);
}

// Observe all cart changes, including removal via a product-row trash button.
// Capture protection identity before the originating event morphs the card.
function handleProtectionCartUpdate(event) {
  const protectionIds = new Set(protectionCards().flatMap((card) => [card.dataset.lineKey, card.dataset.variantId]).filter(Boolean));
  const removedProtection = event.lines?.some((line) => Number(line.quantity) === 0 && protectionIds.has(String(line.id)));
  event.promise?.then(({ detail }) => {
    if (detail?.source === UPDATE_SOURCE || detail?.didError) return;
    if (removedProtection) sessionStorage.setItem(OPT_OUT_KEY, 'true');
    queueProtectionUpdate();
  }).catch(() => {
    // Failed originating mutations must not change customer preferences.
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, handleProtectionCartUpdate);
